import { Injectable } from '@angular/core';
import { Observable, concat, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CsvDocumentInfo, CsvFilesResponse, QueryCsvRequest } from '../../../models/chat.model';
import { ChatCsvSubmitUiResult, ChatItem } from '../../../models/chat-ui.model';
import { ChatService } from '../api/chat.service';
import { CsvTaskPollingService } from '../api/csv-task-polling.service';
import { ChatCsvUiMapperService } from '../mapping/chat-csv-ui-mapper.service';
import { FrontendLoggerService } from '../../frontend-logger.service';
import { ChatIdService } from '../presentation/chat-id.service';
import { PlanLimitError } from '../../../features/plans/models/enforcement.model';
import { PlanLimitErrorService } from '../../../features/plans/services/plan-limit-error.service';

export interface ChatCsvOpenResult {
  pendingQuery: string;
  csvDocuments: CsvDocumentInfo[];
  showCsvModal: boolean;
  error?: string;
}

export interface ChatCsvSelection {
  original_filename: string;
}

export type ChatCsvSubmitEvent =
  | {
      kind: 'started';
      messages: ChatItem[];
    }
  | {
      kind: 'completed';
      uiResult: ChatCsvSubmitUiResult;
    }
  | {
      kind: 'failed';
      message: ChatItem;
    }
  | {
      kind: 'plan-limit';
      error: PlanLimitError;
    };

@Injectable({
  providedIn: 'root'
})
export class ChatCsvFlowService {
  constructor(
    private chatService: ChatService,
    private csvUiMapper: ChatCsvUiMapperService,
    private csvTaskPollingService: CsvTaskPollingService,
    private idService: ChatIdService,
    private planLimitErrorService: PlanLimitErrorService,
    private frontendLogger: FrontendLoggerService
  ) {}

  openCsvModal(query: string): Observable<ChatCsvOpenResult> {
    return this.chatService.getCsvFiles().pipe(
      map(items => ({
        pendingQuery: query,
        csvDocuments: this.mapCsvFiles(items || []),
        showCsvModal: true
      })),
      catchError(error => {
        this.frontendLogger.error(
          'No se pudo obtener la lista de CSV: ' + this.formatError(error),
          'ChatCsvFlowService.openCsvModal'
        );
        return of({
          pendingQuery: query,
          csvDocuments: [],
          showCsvModal: true,
          error: this.formatError(error)
        });
      })
    );
  }

  submitCsvQuery(pendingQuery: string, data: ChatCsvSelection): Observable<ChatCsvSubmitEvent> {
    const startMessages = this.csvUiMapper.buildStartMessages(
      pendingQuery,
      data.original_filename,
      this.idService.createMessageEntityId(),
      this.idService.createMessageEntityId()
    );
    const request: QueryCsvRequest = {
      query: pendingQuery,
      original_filename: data.original_filename
    };

    const submit$ = this.chatService.queryCsv(request).pipe(
      map(response => {
        const planLimit = this.planLimitErrorService.fromCsvResponse(response);
        if (planLimit) {
          return { kind: 'plan-limit', error: planLimit } as ChatCsvSubmitEvent;
        }
        this.frontendLogger.info('Respuesta recibida para consulta CSV', 'ChatCsvFlowService.submitCsvQuery');
        const uiResult = this.csvUiMapper.mapSubmitResponse(response, this.idService.createMessageEntityId());
        if (uiResult.kind === 'queued' && uiResult.taskId) {
          this.csvTaskPollingService.addTask(uiResult.taskId);
        }
        return { kind: 'completed', uiResult } as ChatCsvSubmitEvent;
      }),
      catchError(error => {
        const planLimit = this.planLimitErrorService.fromHttpError(error, 'csv');
        if (planLimit) {
          return of({ kind: 'plan-limit', error: planLimit } as ChatCsvSubmitEvent);
        }
        this.frontendLogger.error(
          'Error al procesar consulta CSV: ' + this.formatError(error),
          'ChatCsvFlowService.submitCsvQuery'
        );
        return of({
          kind: 'failed',
          message: this.buildSystemMessage('Error al procesar consulta CSV')
        } as ChatCsvSubmitEvent);
      })
    );

    return concat(of({ kind: 'started', messages: startMessages } as ChatCsvSubmitEvent), submit$);
  }

  buildCancelMessage(): ChatItem {
    return this.buildSystemMessage('Consulta de analisis CSV cancelada por el usuario.');
  }

  private mapCsvFiles(items: CsvFilesResponse[]): CsvDocumentInfo[] {
    return items.map(item => {
      const resolvedFileName = item.originalFileName || item.fileName || '';
      return {
        created_at: '',
        indexed: true,
        original_filename: resolvedFileName,
        source: resolvedFileName || item.source || '',
        vectors: 0
      };
    });
  }

  private buildSystemMessage(content: string): ChatItem {
    return {
      type: 'system',
      content,
      timestamp: new Date(),
      entityId: this.idService.createMessageEntityId()
    };
  }

  private formatError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message?: unknown }).message || error);
    }

    return String(error);
  }
}
